import { useEffect, useState } from 'react';
import type { Prompt } from '@/types';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { useSetAtom } from 'jotai';
import {
  resetPromptActionAtom,
  updatePromptsActionAtom,
} from '@/stores/settingsStore';
import { capitalizeWords, removeUnderscores } from '@/utils/stringUtils';
import { PromptPreview } from './PromptPreview';

interface PromptItemProps {
  prompt: Prompt;
}

export function PromptItem({ prompt }: PromptItemProps) {
  const isSystem = prompt.role === 'system';
  const [value, setValue] = useState<string>();
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const getInitialValue = () => {
    if (isSystem) {
      return (prompt.placeholders?.role_description as string) ?? '';
    }
    return (prompt.placeholders?.user_prompt as string) ?? '';
  };

  const handleCancel = () => {
    setValue(getInitialValue());
  };

  useEffect(() => {
    setValue(getInitialValue());
  }, [prompt]);

  const updatePrompt = useSetAtom(updatePromptsActionAtom);
  const resetPrompt = useSetAtom(resetPromptActionAtom);

  const handleSave = async () => {
    const key = isSystem ? 'role_description' : 'user_prompt';
    await updatePrompt({
      promptId: prompt.id,
      payload: {
        placeholders: {
          ...prompt.placeholders,
          [key]: value,
        },
      },
    });
  };

  const handleReset = async () => {
    await resetPrompt(prompt.id);
  };

  const previewKey = isSystem ? 'role_description' : 'user_prompt';

  return (
    <div className='space-y-2 rounded-md border border-border p-3'>
      <div className='text-sm font-medium truncate'>
        {capitalizeWords(removeUnderscores(prompt.name))}
      </div>
      <Textarea
        rows={3}
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={
          isSystem ? 'System role description…' : 'User prompt template…'
        }
      />
      <div className='flex flex-wrap justify-between'>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant='destructive' size='sm' type='button'>
              Reset to default
            </Button>
          </AlertDialogTrigger>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset prompt</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to reset this prompt to its default value?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <div className='flex flex-wrap justify-end gap-2 pt-1'>
          <label
            htmlFor={`preview-${prompt.id}`}
            className='inline-flex items-center gap-2 text-sm text-muted-foreground mr-2'
          >
            <input
              id={`preview-${prompt.id}`}
              type='checkbox'
              checked={showPreview}
              onChange={e => setShowPreview(e.target.checked)}
              className='form-checkbox h-4 w-4 rounded'
            />
            Preview
          </label>
          <Button
            variant='outline'
            size='sm'
            type='button'
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button size='sm' type='button' onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
      {showPreview && (
        <PromptPreview
          template={prompt.template}
          placeholderKey={previewKey}
          replacement={value ?? ''}
        />
      )}
    </div>
  );
}
